import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { publicacao as api, type SituacaoDaPublicacao, type VersaoNaLista } from '../api/publicacao'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Campo } from '../componentes/Campo'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { useSessao } from '../auth/useSessao'
import { mensagemDeErro } from './mensagemDeErro'
import { formatar } from '../i18n'
import './usuarios.css'
import './entregas.css'
import './arquivos.css'

/**
 * Publicação e rollback da configuração (demanda 049 · `P-07`).
 *
 * 🔴 **Editar deixou de publicar.** O que se altera nas telas de regras,
 * roteamento e assistente é **rascunho**; o assistente em produção usa a última
 * versão publicada.
 *
 * A tela responde duas perguntas, nessa ordem: *há algo esperando publicação?* e
 * *o que estava valendo quando o cliente reclamou?*
 */
export function Publicacao() {
  const { t } = useTranslation()
  const { pode } = useSessao()

  const podePublicar = pode('ParametroGerenciar')

  const [situacao, setSituacao] = useState<SituacaoDaPublicacao | null>(null)
  const [versoes, setVersoes] = useState<VersaoNaLista[] | null>(null)
  const [observacao, setObservacao] = useState('')
  const [falhou, setFalhou] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [confirmando, setConfirmando] = useState<number | null>(null)

  const carregar = useCallback(async () => {
    setFalhou(false)
    try {
      const [s, v] = await Promise.all([api.situacao(), api.listar()])
      setSituacao(s)
      setVersoes(v)
    } catch {
      setFalhou(true)
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  async function publicar(evento: FormEvent) {
    evento.preventDefault()
    setSalvando(true)
    setErro(null)
    setAviso(null)

    try {
      const r = await api.publicar(observacao.trim())
      setObservacao('')

      // Publicar sem diferença não é erro: é a resposta certa, e a tela diz.
      if (!r.publicada) setAviso(t('publicacao.semDiferenca'))

      await carregar()
    } catch (falha) {
      setErro(mensagemDeErro(falha, t))
    } finally {
      setSalvando(false)
    }
  }

  async function restaurar(numero: number) {
    if (confirmando !== numero) {
      setConfirmando(numero)
      return
    }

    setConfirmando(null)
    setErro(null)

    try {
      await api.restaurar(numero)
      await carregar()
    } catch (falha) {
      setErro(mensagemDeErro(falha, t))
    }
  }

  if (falhou)
    return <Estado tipo="erro" acao={<Botao onClick={carregar}>{t('comum.tentarDeNovo')}</Botao>} />

  if (!situacao || !versoes) return <Estado tipo="carregando" />

  return (
    <section className="usuarios">
      <header className="usuarios__cabecalho">
        <div>
          <h1 className="usuarios__titulo">{t('publicacao.titulo')}</h1>
          <p className="usuarios__descricao">{t('publicacao.descricao')}</p>
        </div>
      </header>

      {erro && <Aviso tom="perigo">{erro}</Aviso>}
      {aviso && <Aviso tom="info">{aviso}</Aviso>}

      {/* 🔴 A pergunta mais importante da tela, respondida antes de tudo. */}
      {situacao.haDiferencaPendente ? (
        <Aviso tom="alerta">{t('publicacao.pendente')}</Aviso>
      ) : (
        <Aviso tom="sucesso">{t('publicacao.emDia')}</Aviso>
      )}

      <Cartao titulo={t('publicacao.vigente')}>
        {situacao.versaoVigente === null ? (
          <p className="entregas__campo">{t('publicacao.nenhuma')}</p>
        ) : (
          <p className="entregas__campo">
            {t('publicacao.vigenteDetalhe', {
              numero: situacao.versaoVigente,
              quem: situacao.publicadaPor,
              quando: situacao.publicadaEm ? formatar.dataHora(situacao.publicadaEm) : '',
            })}
          </p>
        )}

        {podePublicar && (
          <form className="entregas__busca" onSubmit={publicar}>
            <Campo
              rotulo={t('publicacao.observacao')}
              ajuda={t('publicacao.observacaoDica')}
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
            />

            <div className="entregas__acoes">
              <Botao type="submit" carregando={salvando}>
                {t('publicacao.publicar')}
              </Botao>
            </div>
          </form>
        )}
      </Cartao>

      <Cartao titulo={t('publicacao.historico')}>
        {versoes.length === 0 ? (
          <Estado tipo="vazio" titulo={t('publicacao.nenhuma')} />
        ) : (
          <ul className="entregas__lista">
            {versoes.map((v) => (
              <li key={v.numero} className="entregas__item">
                <div className="entregas__dados">
                  <div className="entregas__linha">
                    <code className="entregas__referencia">v{v.numero}</code>
                    <span className="entregas__data">{formatar.dataHora(v.publicadaEm)}</span>
                    {v.numero === situacao.versaoVigente && (
                      <span className="arquivos__categoria">{t('publicacao.emVigor')}</span>
                    )}
                  </div>

                  {v.observacao && <p className="entregas__campo">{v.observacao}</p>}

                  <p className="entregas__autoria">
                    {t('publicacao.publicadaPor', { quem: v.publicadaPor })}
                  </p>

                  {podePublicar && v.numero !== situacao.versaoVigente && (
                    <div className="entregas__acoes">
                      <Botao variante="secundario" onClick={() => restaurar(v.numero)}>
                        {confirmando === v.numero
                          ? t('publicacao.confirmarRestauracao')
                          : t('publicacao.restaurar')}
                      </Botao>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Cartao>
    </section>
  )
}
